/**
 * CI-1 — coach-insight rules engine (task #108; unblocks DD-1/DD-2).
 *
 * The /v2/today coach surfaces (desktop `CoachCard`, mobile `CoachStories`)
 * were demo-gated because their fixture copy was fabricated ("Swap shoes at
 * 287 mi", "race week" to a pre-season account). This module is the missing
 * real source: a PURE, DETERMINISTIC rules engine — no LLM, no endpoint, no
 * migration. Every insight cites ONLY data already loaded on today.tsx; when
 * there's nothing real to say, nothing fires and the surfaces stay hidden.
 *
 * Modeled on `proactiveTriggers.ts`: pure detectors + injected input +
 * injected `now`. Per-rule functions are exported for unit tests; callers go
 * through `evaluateCoachRules` + the two adapters.
 *
 * NO GEAR RULE on purpose — we have no gear data, so Gair stays silent
 * (the shoe-mileage fixture was exactly the fabrication being removed).
 */

import { COACH_DISPLAY, type CoachId, type CoachMessage } from './coachMessages';
import { daysSince, todayLocalIsoDate } from './proactiveTriggers';
import type { CoachInsight } from '../components/CoachStories/CoachStories';

// --- Input ----------------------------------------------------------------

/** Today's session, minimal structural slice of `TodaySession`. */
export interface InsightSession {
  intensityLabel: string;
  titleLead: string;
  titleEm: string;
  titleTrail: string;
  subtitle: string;
}

/** One day of the current week, minimal structural slice of `Day`. */
export interface InsightWeekDay {
  rest?: boolean;
  completionStatus?: 'completed' | 'skipped' | 'upcoming' | null;
}

/** Current week, minimal structural slice of `WeeklyPlan`. */
export interface InsightWeek {
  days: InsightWeekDay[];
  weekMileageDone: number;
  weekMileageTarget: number;
  /** B338 — the same two figures in the athlete's own unit (`useWeeklyPlan`). Absent
   *  or 'mi' reads `weekMileage*` as before. */
  distanceUnit?: 'mi' | 'km';
  weekDistanceDone?: number;
  weekDistanceTarget?: number;
}

/** One race result, minimal structural slice of the `useRaceResults` row. */
export interface InsightRaceResult {
  is_pr: boolean;
  race_date: string; // 'YYYY-MM-DD'
  distance: string;
  finish_time: string;
  event_name: string;
}

/**
 * Everything the rules may cite. The caller (today.tsx) builds this from
 * data hooks it ALREADY loads — the engine adds zero fetches. All fields
 * accept their loading/absent shapes (null / empty) and rules no-fire on
 * them; the caller should still hold the engine back until loads settle
 * so a quiet surface means "nothing to say", not "not loaded yet".
 */
export interface CoachInsightInput {
  session: InsightSession | null;
  week: InsightWeek | null;
  /** Next upcoming race, from `useUpcomingRace`'s adapted shape. */
  race: { name: string; daysAway: number } | null;
  currentWeekNumber: number | null;
  totalWeeks: number;
  draftedWeeks: ReadonlySet<number>;
  currentStreak: number;
  thisWeekCompliancePct: number | null;
  lastWeekCompliancePct: number | null;
  /** Reverse-chronological (most recent first), as `useRaceResults` returns. */
  raceResults: ReadonlyArray<InsightRaceResult>;
  /** Monday-based 0..6 (`todayMondayBasedLocal()`). */
  todayDayOfWeek: number;
  /**
   * Piece 2 — whether the plan-page batch action actually exists (the server
   * `batch_authoring` flag). The batch nudge frame must never point at a CTA
   * that doesn't render; absent/false → the single-week chat frame (which
   * works regardless of the flag) is used. Optional so older callers stay valid.
   */
  batchDraftAvailable?: boolean;
}

// --- Result ----------------------------------------------------------------

export interface CoachRuleResult {
  id: string;
  coach: CoachId;
  /** Higher fires first. Distinct per rule. */
  priority: number;
  /** One sentence for the mobile CoachStories card. */
  storyText: string;
  /** Headline for the desktop CoachCard (clipped to 60 chars). */
  title: string;
  /** 1–2 sentences. Advice only — never a data claim the input can't back. */
  body: string;
  cta: { label: string; href: string };
}

const TITLE_MAX = 60;

function clipTitle(s: string): string {
  return s.length <= TITLE_MAX ? s : `${s.slice(0, TITLE_MAX - 1)}…`;
}

function isRestSession(session: InsightSession | null): boolean {
  return session !== null && session.intensityLabel === 'Rest';
}

// --- The 8 rules (priority DESC) -------------------------------------------

/**
 * R1 · race-countdown (race, 90): a real upcoming race within 7 days.
 *
 * B268 — `storyText` seeds a persisted assistant chat turn (`CoachStories.tsx`
 * `coachCard.text`), and a teammate/trainer can name a team meet anything up
 * to 200 characters (`api/lib/team/...` meet creation has no copy review).
 * The card must never carry that name into another athlete's chat, so the
 * line names no race, for every race — a template line only (founder
 * decision, 2026-09-27).
 */
export function ruleRaceCountdown(input: CoachInsightInput): CoachRuleResult | null {
  const { race } = input;
  if (!race || race.daysAway < 0 || race.daysAway > 7) return null;
  const when =
    race.daysAway === 0 ? 'today' : race.daysAway === 1 ? 'tomorrow' : `in ${race.daysAway} days`;
  const line = `Your next meet is ${when}.`;
  return {
    id: 'race-countdown',
    coach: 'race',
    priority: 90,
    storyText: line,
    title: clipTitle(line),
    // Advice only — we know the date, not the course.
    body: 'Nothing new this week. Trust the work, keep the easy days easy, and arrive rested.',
    cta: { label: 'Open races', href: '/v2/races' },
  };
}

/** R2 · pr-celebration (race, 80): most recent PR, set within the last 7 days. */
export function rulePrCelebration(
  input: CoachInsightInput,
  now: Date = new Date(),
): CoachRuleResult | null {
  const pr = input.raceResults.find((r) => r.is_pr);
  if (!pr || daysSince(pr.race_date, now) > 7) return null;
  const line = `New ${pr.distance} PR: ${pr.finish_time}.`;
  return {
    id: 'pr-celebration',
    coach: 'race',
    priority: 80,
    storyText: line,
    title: clipTitle(line),
    body: `Set at ${pr.event_name}. That fitness is real, bank it.`,
    cta: { label: 'Open races', href: '/v2/races' },
  };
}

/**
 * R3 · compliance-drop (training, 70): this week is sharply down vs last.
 * Monday-morning guard: an empty Monday/Tuesday always reads "down" —
 * only speak from mid-week (todayDayOfWeek ≥ 3, i.e. Thursday onward).
 */
export function ruleComplianceDrop(input: CoachInsightInput): CoachRuleResult | null {
  const { thisWeekCompliancePct: thisWeek, lastWeekCompliancePct: lastWeek } = input;
  if (thisWeek === null || lastWeek === null) return null;
  if (!(thisWeek < 50 && lastWeek > 70)) return null;
  if (input.todayDayOfWeek < 3) return null;
  const line = `This week ${thisWeek}% vs ${lastWeek}% last week.`;
  return {
    id: 'compliance-drop',
    coach: 'training',
    priority: 70,
    storyText: line,
    title: clipTitle(line),
    body: 'A reset beats a write-off. Tell Airo what got in the way and re-shape the rest of the week.',
    cta: { label: 'Ask Airo', href: '/v2/chat' },
  };
}

/**
 * R4 · draft-next-week (training, 60): next week is in the arc but undrafted.
 *
 * Piece 2 (Phase B) — batch-refresh reframe. When next week is undrafted AND ≥2
 * undrafted in-arc weeks remain from next-week forward AND the batch action is
 * AVAILABLE (`batchDraftAvailable` — the server batch_authoring flag), the nudge
 * reframes to the BATCH action → `/v2/plan` (the plan-page batch CTA), NOT the
 * single-week chat deep-link. The ≥2 threshold matches the plan-page CTA gate,
 * so the two surfaces agree; the flag gate guarantees the nudge never points at
 * a CTA that doesn't render (flag off / rollback → single-week frame, which
 * works regardless). With only ONE undrafted week left there is nothing to
 * batch. It self-dedups: once a batch is drafted, next week is drafted, so the
 * rule goes quiet.
 */
export function ruleDraftNextWeek(input: CoachInsightInput): CoachRuleResult | null {
  const { currentWeekNumber, totalWeeks, draftedWeeks } = input;
  if (currentWeekNumber === null) return null;
  const next = currentWeekNumber + 1;
  if (next > totalWeeks || draftedWeeks.has(next)) return null;

  // Count undrafted in-arc weeks from `next` forward — the batch's target set.
  let undraftedAhead = 0;
  for (let wk = next; wk <= totalWeeks; wk++) {
    if (!draftedWeeks.has(wk)) undraftedAhead++;
  }

  if (undraftedAhead >= 2 && input.batchDraftAvailable === true) {
    // Batch frame — ONLY when the plan-page batch action exists (batch_authoring
    // ON), else this would point at a CTA that doesn't render. The plan builds in
    // ~4-week blocks so it stays matched to the athlete's real captured data. No
    // fabricated numbers, and no claim about the CURRENT week's drafted state
    // (it may itself be undrafted — only `next` is known-undrafted here).
    const line = 'Your next few weeks aren’t drafted yet.';
    return {
      id: 'draft-next-week',
      coach: 'training',
      priority: 60,
      storyText: line,
      title: clipTitle(line),
      body: 'Draft your next few weeks so your plan stays matched to how you’re actually training.',
      cta: { label: 'Draft the next weeks', href: '/v2/plan' },
    };
  }

  // Single-week frame — exactly one undrafted week left; nothing to batch.
  const line = `Week ${next} isn't drafted yet.`;
  return {
    id: 'draft-next-week',
    coach: 'training',
    priority: 60,
    storyText: line,
    title: clipTitle(line),
    body: 'Draft it now so next week starts decided, not improvised.',
    // Matches parseDeepLinkWeek on /v2/chat (P3-2 deep link).
    cta: { label: `Draft week ${next}`, href: `/v2/chat?draftWeek=${next}` },
  };
}

/** R5 · streak-protection (mental, 50): a live streak + a session today. */
export function ruleStreakProtection(input: CoachInsightInput): CoachRuleResult | null {
  if (input.currentStreak < 3 || input.session === null || isRestSession(input.session)) return null;
  const line = `${input.currentStreak}-day streak: today keeps it alive.`;
  return {
    id: 'streak-protection',
    coach: 'mental',
    priority: 50,
    storyText: line,
    title: clipTitle(line),
    body: 'Show up easy if you must. Consistency is the whole trick.',
    cta: { label: 'Ask Kairu', href: '/v2/chat' },
  };
}

/** R6 · rest-day-reframe (recovery, 40): today IS the rest day. */
export function ruleRestDayReframe(input: CoachInsightInput): CoachRuleResult | null {
  if (!isRestSession(input.session)) return null;
  const line = "Rest day: that's the workout.";
  return {
    id: 'rest-day-reframe',
    coach: 'recovery',
    priority: 40,
    storyText: line,
    title: line,
    body: 'Adaptation happens when you stop. Eat well, sleep early, leave the shoes by the door.',
    cta: { label: 'Ask Onai', href: '/v2/chat' },
  };
}

/** R7 · week-progress (training, 30): at least one session done this week. */
export function ruleWeekProgress(input: CoachInsightInput): CoachRuleResult | null {
  const { week, currentWeekNumber } = input;
  if (!week) return null;
  const training = week.days.filter((d) => !d.rest);
  const done = training.filter((d) => d.completionStatus === 'completed').length;
  if (training.length === 0 || done < 1) return null;
  const line = `${done} of ${training.length} sessions done this week.`;
  // B338 — a km athlete's "on the board" line states km, from the adapter's own-unit pair.
  const inKm =
    week.distanceUnit === 'km' &&
    typeof week.weekDistanceDone === 'number' &&
    typeof week.weekDistanceTarget === 'number';
  const boardDone = inKm ? week.weekDistanceDone : week.weekMileageDone;
  const boardTarget = inKm ? week.weekDistanceTarget : week.weekMileageTarget;
  return {
    id: 'week-progress',
    coach: 'training',
    priority: 30,
    storyText: line,
    title: clipTitle(line),
    body:
      week.weekMileageTarget > 0
        ? `${boardDone} of ${boardTarget} ${inKm ? 'km' : 'mi'} on the board.`
        : 'Keep stacking them.',
    cta: {
      label: 'Open week',
      href: currentWeekNumber !== null ? `/v2/plan/week/${currentWeekNumber}` : '/v2/plan',
    },
  };
}

/** R8 · today-session (training, 20): there IS a session today — name it. */
export function ruleTodaySession(input: CoachInsightInput): CoachRuleResult | null {
  const { session } = input;
  if (session === null || isRestSession(session)) return null;
  // "Easy run," + "3 miles" + "." → "Easy run, 3 miles." — and an empty
  // titleEm must not leave a gap before punctuation ("Easy run ." — seen
  // live on the CI-2 smoke; Ramiro's session had no em phrase).
  const joined = `${session.titleLead} ${session.titleEm}${session.titleTrail}`
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();
  const line = `Today: ${joined}`;
  return {
    id: 'today-session',
    coach: 'training',
    priority: 20,
    storyText: line,
    title: clipTitle(line),
    body: session.subtitle,
    cta: { label: 'Ask Airo', href: '/v2/chat' },
  };
}

// --- Engine -----------------------------------------------------------------

/**
 * Run all 8 rules; return the fired ones ordered priority DESC (id ASC on
 * a tie). Deterministic: same input + now → same array.
 */
export function evaluateCoachRules(
  input: CoachInsightInput,
  now: Date = new Date(),
): CoachRuleResult[] {
  return [
    ruleRaceCountdown(input),
    rulePrCelebration(input, now),
    ruleComplianceDrop(input),
    ruleDraftNextWeek(input),
    ruleStreakProtection(input),
    ruleRestDayReframe(input),
    ruleWeekProgress(input),
    ruleTodaySession(input),
  ]
    .filter((r): r is CoachRuleResult => r !== null)
    .sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id));
}

// --- Adapters ----------------------------------------------------------------

/** Mobile CoachStories shows at most this many cards. */
export const MAX_STORY_INSIGHTS = 4;

/** Glyph per coach — matches the `ramiroCoachInsights` fixture (race = 'P'). */
const COACH_GLYPH: Record<CoachId, string> = {
  training: 'T',
  gear: 'G',
  race: 'P',
  recovery: 'R',
  mental: 'M',
};

/** Fired rules → mobile `CoachStories` cards (top `MAX_STORY_INSIGHTS`). */
export function toCoachInsights(results: CoachRuleResult[]): CoachInsight[] {
  return results.slice(0, MAX_STORY_INSIGHTS).map((r) => ({
    id: r.id,
    label: COACH_DISPLAY[r.coach].label,
    color: `var(--c-${r.coach})`,
    glyph: COACH_GLYPH[r.coach],
    text: r.storyText,
  }));
}

/** Top fired rule → the desktop `CoachCard` message. Null when quiet. */
export function toCoachMessage(
  results: CoachRuleResult[],
  now: Date = new Date(),
): CoachMessage | null {
  const top = results[0];
  if (!top) return null;
  const display = COACH_DISPLAY[top.coach];
  return {
    id: `rule-${top.id}-${todayLocalIsoDate(now)}`,
    coach: top.coach,
    trigger: { kind: 'active', ruleId: top.id },
    kicker: `${display.label} · ${display.name}`,
    rightMeta: 'today',
    title: top.title,
    body: top.body,
    cta: top.cta,
    expiresAt: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
  };
}
